'use client'

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { FaTimes, FaChevronUp } from 'react-icons/fa'

import { useBottomScrollListener } from 'react-bottom-scroll-listener'
import { safeGetItem, safeSetItem } from '../helpers/safeStorage'
import Slide from './immersive/Slide'
import { CLOSE_INSET, ROUND_BTN } from './immersive/styles'

// Easing for the one-time "swipe up" peek nudge (mirrors the /generate feed).
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3)
const easeInOutCubic = (t) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2

// localStorage stamp for the swipe hint. Re-hints every 12h rather than once ever:
// a single showing on the very first open is easy to miss (or to forget), and users
// who never learn the viewer scrolls fall back to tapping tile-by-tile. A legacy '1'
// from the once-ever version reads as long-expired, so those browsers get one more
// hint and then start stamping real timestamps.
const NUDGE_SEEN_KEY = 'reelsSwipeNudgeSeen'
const NUDGE_REPEAT_MS = 12 * 60 * 60 * 1000

// Preload media within this many slides of the active one (each direction) so
// swiping feels instant; everything else stays lazy to keep bandwidth low.
const PRELOAD_RADIUS = 2

// Virtualization: only the active slide ± this many are mounted as full Slides;
// the rest render as cheap same-height placeholders. Keeps open/close fast and
// memory bounded even when the feed holds hundreds of items.
const WINDOW_RADIUS = 4

export default function ImmersiveFeed({
  items = [],
  startIndex = 0,
  onClose,
  onLoadMore,
  safe = false,
  user,
}) {
  const { t } = useTranslation()
  // Per-viewer-session cache of liked status (id -> bool), shared across slides so
  // virtualized re-mounts don't re-fetch check_liked_image. Cleared on unmount.
  const likedCacheRef = useRef(new Map())
  const [activeIndex, setActiveIndex] = useState(startIndex)
  const [muted, setMuted] = useState(true)

  // One-time "swipe up" hint (TikTok-style): `nudgeIn` drives the opacity fade,
  // `nudgeShown` keeps it mounted through the fade-out. `nudgeLiveRef` gates the
  // dismiss so repeat calls (and the auto-hide) are no-ops after it's gone.
  const [nudgeShown, setNudgeShown] = useState(false)
  const [nudgeIn, setNudgeIn] = useState(false)
  const nudgeLiveRef = useRef(false)
  const dismissNudge = useCallback(() => {
    if (!nudgeLiveRef.current) return
    nudgeLiveRef.current = false
    setNudgeIn(false)
    setTimeout(() => setNudgeShown(false), 300)
  }, [])

  // Infinite scroll bound to THIS container (the page's window listener can't
  // fire while the fixed overlay covers the viewport).
  const scrollRef = useBottomScrollListener(() => onLoadMore && onLoadMore(), {
    offset: 1400,
    debounce: 600,
  })

  // Fixed full-screen scroll lock (reuses the generate feed's iOS-safe class).
  useEffect(() => {
    const html = document.documentElement
    const scrollY = window.scrollY
    document.body.style.setProperty('--pc-native-lock-top', `-${scrollY}px`)
    html.classList.add('pc-native-lock')
    document.body.classList.add('pc-native-lock')
    const prevOverscroll = html.style.overscrollBehavior
    html.style.overscrollBehavior = 'none'
    return () => {
      html.classList.remove('pc-native-lock')
      document.body.classList.remove('pc-native-lock')
      document.body.style.removeProperty('--pc-native-lock-top')
      html.style.overscrollBehavior = prevOverscroll
      window.scrollTo(0, scrollY)
    }
  }, [])

  // Publish the page height so media caps to exactly one viewport (re-fits as the
  // browser URL bar collapses/expands).
  useLayoutEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const apply = () =>
      el.style.setProperty('--feed-media-max', `${el.clientHeight}px`)
    apply()
    window.addEventListener('resize', apply)
    let ro
    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(apply)
      ro.observe(el)
    }
    return () => {
      window.removeEventListener('resize', apply)
      ro?.disconnect()
    }
  }, [scrollRef])

  // Open exactly on the tapped item, before paint (no flash).
  useLayoutEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = startIndex * el.clientHeight
    // Run once on mount.
  }, [])

  // Hint that the feed is swipeable — fade in a "Swipe up" pill and (unless
  // reduced-motion) peek the next reel and ease it back, twice, just like the
  // /generate feed's nudge. Fires at most once per 12h per browser, and only when
  // there's actually a reel below to reveal. Any real interaction dismisses it early.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const lastSeen = Number(safeGetItem(NUDGE_SEEN_KEY))
    if (Number.isFinite(lastSeen) && Date.now() - lastSeen < NUDGE_REPEAT_MS)
      return
    if (items.length <= startIndex + 1) return // nothing below to hint at
    safeSetItem(NUDGE_SEEN_KEY, String(Date.now()))

    nudgeLiveRef.current = true
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

    const showTimer = setTimeout(() => {
      setNudgeShown(true)
      requestAnimationFrame(() => setNudgeIn(true))
    }, 650)
    // Stays up long enough to outlast the two peek beats below and still be read.
    const hideTimer = setTimeout(dismissNudge, 5200)

    let raf = 0
    let peekTimer = 0
    if (!reduce) {
      peekTimer = setTimeout(() => {
        const base = startIndex * el.clientHeight
        // Bail if the user already scrolled, or there's no room to peek.
        if (el.scrollTop > base + 4) return
        // Deeper than a token wobble: enough of the next reel appears that it reads
        // as "there is another one under this", not as a rendering glitch.
        const peek = Math.min(el.clientHeight * 0.14, 84)
        if (el.scrollHeight - base - el.clientHeight < peek) return
        const prevSnap = el.style.scrollSnapType
        el.style.scrollSnapType = 'none'
        const start = performance.now()
        const RISE = 430
        const FALL = 540
        const CYCLE = RISE + FALL
        // Two beats — one is easy to miss if you glance away as the viewer opens.
        const BEATS = 2
        const restore = () => {
          el.scrollTop = base
          el.style.scrollSnapType = prevSnap
        }
        const tick = (now) => {
          // A real swipe/wheel/key clears nudgeLiveRef — stop animating instantly
          // rather than fighting the user's own scroll for the remaining beat.
          if (!nudgeLiveRef.current) return restore()
          const e = now - start
          if (e >= CYCLE * BEATS) return restore()
          const phase = e % CYCLE
          el.scrollTop =
            phase < RISE
              ? base + peek * easeOutCubic(phase / RISE)
              : base + peek * (1 - easeInOutCubic((phase - RISE) / FALL))
          raf = requestAnimationFrame(tick)
        }
        raf = requestAnimationFrame(tick)
      }, 800)
    }

    return () => {
      clearTimeout(showTimer)
      clearTimeout(hideTimer)
      clearTimeout(peekTimer)
      cancelAnimationFrame(raf)
    }
    // Mount-only.
  }, [])

  // Which item is centered right now — derived from the scroll position (exact at
  // close time, unlike the IO-driven activeIndex which can lag a frame). Handed
  // back to the grid on close so it can scroll that tile into view.
  const itemsRef = useRef(items)
  itemsRef.current = items
  const getActiveId = useCallback(() => {
    const el = scrollRef.current
    const list = itemsRef.current
    if (!list?.length) return null
    const i =
      el && el.clientHeight
        ? Math.round(el.scrollTop / el.clientHeight)
        : activeIndex
    return list[Math.max(0, Math.min(list.length - 1, i))]?.id ?? null
  }, [scrollRef, activeIndex])

  // Browser/hardware back closes the viewer; Esc too.
  const requestClose = useCallback(() => {
    if (typeof window !== 'undefined' && window.history.state?.pcImmersive) {
      window.history.back()
    } else {
      onClose?.(getActiveId())
    }
  }, [onClose, getActiveId])

  useEffect(() => {
    window.history.pushState({ pcImmersive: true }, '')
    const onPop = () => onClose?.(getActiveId())
    const onKey = (e) => {
      if (e.key === 'Escape') {
        requestClose()
        return
      }
      // Desktop keyboard navigation between reels (snap settles on the neighbour).
      const el = scrollRef.current
      if (!el) return
      if (e.key === 'ArrowDown' || e.key === 'PageDown') {
        e.preventDefault()
        dismissNudge()
        el.scrollBy({ top: el.clientHeight, behavior: 'smooth' })
      } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
        e.preventDefault()
        dismissNudge()
        el.scrollBy({ top: -el.clientHeight, behavior: 'smooth' })
      }
    }
    // Desktop trackpad/mouse-wheel also counts as "I know it scrolls".
    const onWheel = () => dismissNudge()
    window.addEventListener('popstate', onPop)
    window.addEventListener('keydown', onKey)
    window.addEventListener('wheel', onWheel, { passive: true })
    return () => {
      window.removeEventListener('popstate', onPop)
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('wheel', onWheel)
    }
    // Mount-only: push one history entry so back/gesture closes the viewer.
  }, [])

  // Details: open the full detail page in a NEW tab (mirrors the desktop grid's
  // window.open) so the viewer stays put and the user keeps their scroll spot.
  const openDetails = useCallback((id) => {
    window.open(`/image/${id}`, '_blank', 'noopener,noreferrer')
  }, [])

  const toggleMuted = useCallback(() => setMuted((m) => !m), [])

  // Derive the active page from scroll position (rAF-throttled). This is the
  // single source of `activeIndex`, so it works even over virtualized placeholder
  // slides (which have no IntersectionObserver of their own).
  const scrollTick = useRef(false)
  const onScroll = useCallback(() => {
    if (scrollTick.current) return
    scrollTick.current = true
    requestAnimationFrame(() => {
      scrollTick.current = false
      const el = scrollRef.current
      if (!el || !el.clientHeight) return
      const i = Math.max(
        0,
        Math.min(items.length - 1, Math.round(el.scrollTop / el.clientHeight)),
      )
      setActiveIndex((prev) => (prev === i ? prev : i))
    })
  }, [scrollRef, items.length])

  // Lightweight pull-down-to-close when already at the top.
  const touch = useRef({ y: 0, top: 0, fired: false })
  const onTouchStart = (e) => {
    dismissNudge()
    touch.current = {
      y: e.touches[0].clientY,
      top: scrollRef.current?.scrollTop || 0,
      fired: false,
    }
  }
  const onTouchMove = (e) => {
    const tc = touch.current
    if (tc.fired || tc.top > 0) return
    if (e.touches[0].clientY - tc.y > 90) {
      tc.fired = true
      requestClose()
    }
  }

  // Client-only (never rendered during SSR: the pages only mount this once a tile
  // is clicked, i.e. viewer.open flips true). Guard keeps createPortal safe.
  if (typeof document === 'undefined') return null

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[9999] bg-black"
    >
      {/* Fixed close (a viewer action, not an image action, so it stays put) */}
      <button
        type="button"
        aria-label="Close"
        onClick={requestClose}
        className={`absolute z-40 ${ROUND_BTN}`}
        style={CLOSE_INSET}
      >
        <FaTimes size={20} />
      </button>

      {/* "Swipe up" hint (at most once per 12h) — bounces in the lower third, fades
          out on any real interaction or after a few seconds. pointer-events-none so
          it never intercepts a swipe. */}
      {nudgeShown && (
        <div
          className={`pointer-events-none absolute inset-x-0 z-40 flex justify-center transition-opacity duration-300 ${
            nudgeIn ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ bottom: '30%' }}
        >
          <div className="flex flex-col items-center gap-1 rounded-2xl bg-black/65 px-5 py-3.5 shadow-[0_4px_20px_rgba(0,0,0,0.6)] ring-1 ring-white/25 backdrop-blur-sm">
            <FaChevronUp className="animate-bounce text-2xl text-white motion-reduce:animate-none" />
            <span className="text-sm font-semibold text-white">
              {t('swipeForMore', 'Swipe up for more')}
            </span>
          </div>
        </div>
      )}

      <div
        ref={scrollRef}
        onScroll={onScroll}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        className="h-[100dvh] w-full snap-y snap-mandatory overflow-y-auto overscroll-y-contain"
        style={{ scrollbarWidth: 'none' }}
      >
        {items.map((item, index) => {
          // Virtualized: far slides render as cheap same-height placeholders so
          // opening/closing a long feed doesn't mount/unmount hundreds of trees.
          if (Math.abs(index - activeIndex) > WINDOW_RADIUS) {
            return (
              <div
                key={`${item.id}-${index}`}
                data-idx={index}
                className="h-[100dvh] w-full snap-start"
              />
            )
          }
          return (
            <Slide
              key={`${item.id}-${index}`}
              item={item}
              index={index}
              active={index === activeIndex}
              near={Math.abs(index - activeIndex) <= 1}
              preload={Math.abs(index - activeIndex) <= PRELOAD_RADIUS}
              muted={muted}
              onToggleMute={toggleMuted}
              user={user}
              safe={safe}
              onOpenDetails={openDetails}
              likedCache={likedCacheRef.current}
            />
          )
        })}
      </div>
    </div>,
    document.body,
  )
}
